import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import QuickSearchHakiSafety from './HS/QuickSearchHakiSafety';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import Footer from './Haki/Components/Footer';
import FooterHs from './Haki/Components/FooterHs';
import VatToggle from './Haki/Components/VatToggle';
import Accordion from './Haki/Components/Accordion';
import GlobeItem from './Components/GlobeItem';
import ProductBlockScrollButtons from './Haki/Components/ProductBlockScrollButtons/ProductBlockScrollButtons';
import { toggleTopHeader } from './Haki/Components/ShowMoreToggle';
import Recaptcha from './Haki/Components/Recaptcha';
import CategoryHeader from './Haki/Components/CategoryHeader';
import RenderPrivateOrBusinessFooterSelector from '../Scripts/Components/RenderPrivateOrBusinessFooterSelecetor'
import ComplementaryProducts from './Haki/Components/ComplementaryProducts';

import { header } from './header';
import { breadcrumbs } from './breadcrumbs';
import { clickableNav } from './clickableNav';
import { boardcards } from './boardcards';
import GtmTracker from './Components/GtmTracker';

window.onload = function () {
    toggleTopHeader();
};

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {

    window.gtmTracker = GtmTracker();
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('quickSearch-hs')) {
        renderReact(
            <Provider store={store}>
                <QuickSearchHakiSafety />
            </Provider>,
            document.getElementById('quickSearch-hs')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('footer')) {
        renderReact(
            <Provider store={store}>
                <Footer />
            </Provider>,
            document.getElementById('footer')
        );
    }
    if (document.getElementById('footer-hs')) {
        renderReact(
            <Provider store={store}>
                <FooterHs />
            </Provider>,
            document.getElementById('footer-hs')
        );
    }
    if (document.getElementById('accordion')) {
        renderReact(
            <Provider store={store}>
                <Accordion />
            </Provider>,
            document.getElementById('accordion')
        );
    }
    const vatToggle = document.getElementById('vatToggle');
    if (vatToggle) {
        const toggle = vatToggle.dataset.toggle === 'True';
        renderReact(<VatToggle toggle={toggle} />, vatToggle);
    }

    if (document.getElementById('language-globe')) {
        renderReact(
            <Provider store={store}>
                <GlobeItem />
            </Provider>,
            document.getElementById('language-globe')
        );
    }

    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        const element = document.getElementById('checkout');
        const toggle = element.dataset.toggle === 'True';
        renderReact(
            <Provider store={store}>
                <Checkout toggle={toggle} />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const ImageSlider = DynamicComponent({
            loader: () => import('./Haki/Components/ImageSlider/ImageSlider'),
        });

        const rootElement = document.getElementById('lightBoxImages');
        const images = Array.from(rootElement.querySelectorAll('template')).map(
            (img) => ({
                src: img.dataset.src,
            })
        );

        renderReact(
            <Provider store={store}>
                <ImageSlider images={images} />
            </Provider>,
            document.getElementById('lightBoxImages')
        );
    }
    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [...slider.querySelectorAll('template')].map((block) => {
                    return {
                        html: block.innerHTML,
                    };
                });
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            }
        );
    }
    if (document.getElementById('product-block-scroll-Accessory')) {
        const id = 'Accessory';
        renderReact(
            <Provider store={store}>
                <ProductBlockScrollButtons scrollElementId={id} />
            </Provider>,
            document.getElementById('product-block-scroll-Accessory')
        );
    }
    if (
        document.getElementById('product-block-scroll-DirectionalSimilarProducts')
    ) {
        const id = 'DirectionalSimilarProducts';
        renderReact(
            <Provider store={store}>
                <ProductBlockScrollButtons scrollElementId={id} />
            </Provider>,
            document.getElementById('product-block-scroll-DirectionalSimilarProducts')
        );
    }
    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
            const {
                articleNumber,
                quantityFieldId,
                href,
                cssClass,
                label,
            } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <BuyButton
                        {...{
                            label,
                            articleNumber,
                            quantityFieldId,
                            href,
                            cssClass,
                        }}
                    />
                </Provider>,
                button
            );
        });
    }
    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }

    if (document.querySelectorAll('.js-toggle-class').length > 0) {
        const itemsToToggle = document.querySelectorAll('.js-toggle-class');
        const onToggleClick = (e) => {
            let item = e.currentTarget;
            item.classList.toggle(item.dataset.toggleclass);
        };

        itemsToToggle.forEach((item) => {
            item.addEventListener('click', onToggleClick);
        });
    }

    if (document.querySelectorAll('Recaptcha').length > 0) {
        Array.from(document.querySelectorAll('Recaptcha')).forEach((recaptcha) => {
            const { submitId } = recaptcha.dataset;
            renderReact(
                <Provider store={store}>
                    <Recaptcha submitId={submitId} />
                </Provider>,
                recaptcha
            );
        });
    }

    const renderPrivateOrBusinessPopupEl = document.getElementById(
        'renderPrivateOrBusinessPopup'
    );
    if (renderPrivateOrBusinessPopupEl) {
        const RenderPrivateOrBusinessPopup = DynamicComponent({
            loader: () => import('./Components/RenderPrivateOrBusinessPopup'),
        });
        renderReact(
            <Provider store={store}>
                <RenderPrivateOrBusinessPopup />
            </Provider>,
            renderPrivateOrBusinessPopup
        );
    }

    if (document.getElementById('renderPrivateOrBusinessFooterSelector')) {
        renderReact(
            <Provider store={store}>
                <RenderPrivateOrBusinessFooterSelector />
            </Provider>,
            document.getElementById('renderPrivateOrBusinessFooterSelector')
        );
    }

    if (document.getElementById('smallHeaderCategoryPage')) {
        let elem = document.getElementById('smallHeaderCategoryPage');
        let description = elem.getAttribute('data-description');
        let usps = elem.dataset.usp;
        let linkText = elem.getAttribute('data-link-text');

        renderReact(
            <Provider store={store}>
                <CategoryHeader
                    description={description}
                    usps={JSON.parse(usps)}
                    linkText={linkText}
                />
            </Provider>,
            document.getElementById('smallHeaderCategoryPage')
        );
    }
    var visualizerElem = document.getElementById('visualizer');
    if (visualizerElem) {
        const Visualizer = DynamicComponent({
            loader: () => import('./Components/Visualizer'),
        });
        renderReact(
            <Provider store={store}>
                <Visualizer />
            </Provider>,
            visualizerElem
        );
    }
};

bootstrapComponents();
